import { createContext, useContext, useState } from 'react'
import { ImagePickerContextValue, ImagePickerProviderProps } from './ImagePickerContextTypes'
import { SelectedImage } from 'types'
import { DrawerProps } from 'components/Drawer/Root'
import { ACCEPTED_IMAGE_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import { useConfirmModal } from 'hooks/useConfirmModal'
import { AiConfirmModal } from './AIGeneration/AiConfirmModal'
import { useConfirmDrawer } from 'hooks/useConfirmDrawer'
import { AiConfirmDrawer } from 'components/ImagePickerModal/AIGeneration/AiConfirmDrawer'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

export const ImagePickerContext = createContext<ImagePickerContextValue & DrawerProps>({
  selectedImages: [],
  hiddenProviders: new Set(),
  provider: 'uploads',
  setSelectedImages: () => {},
  toggleSelectedImage: () => {},
  setProvider: () => {},
  onConfirm: () => {},
  onCancel: () => {},
  onUploadStart: async () => {},
  uploadImages: async () => [],
  isUploading: false,
  initialQuery: '',
  maxCount: Infinity,
  accept: '',
  isAIGenerating: false,
  setIsAIGenerating: () => {},
})

const ImagePickerContextProvider = ImagePickerContext.Provider

export const ImagePickerProvider = ({ showAiConfirm = true, ...props }: ImagePickerProviderProps & DrawerProps) => {
  const [selectedImages, setSelectedImages] = useState<SelectedImage[]>([])
  const [isAIGenerating, setIsAIGenerating] = useState<boolean>(false)
  const [provider, _setProvider] = useState(props.defaultProvider)
  const [isUploading, setIsUploading] = useState(false)
  const maxCount = props.maxCount || Infinity
  const accept = props.accept || ACCEPTED_IMAGE_MIME_TYPES
  const hiddenProviders = props.hiddenProviders || new Set()
  const aiConfirmProvider = useConfirmModal(AiConfirmModal)
  const mobileAiConfirmProvider = useConfirmDrawer(AiConfirmDrawer)
  const { isMobile } = useResponsiveDesign()

  const toggleSelectedImage = (image: SelectedImage) => {
    setSelectedImages((images: SelectedImage[]) => {
      if (images.some(({ url }) => url === image.url)) {
        return images.filter(({ url }) => url !== image.url)
      }

      if (selectedImages.length >= maxCount) {
        return [...images.slice(1), image]
      }

      return [...images, image]
    })
  }

  return (
    <ImagePickerContextProvider
      value={{
        selectedImages,
        provider,
        hiddenProviders,
        setSelectedImages,
        toggleSelectedImage,
        setProvider: async (newProvider) => {
          if (newProvider && hiddenProviders.has(newProvider)) {
            return
          }

          if (
            provider === 'ai' &&
            provider !== newProvider &&
            showAiConfirm &&
            (selectedImages.length > 0 || isAIGenerating)
          ) {
            const confirm = await (isMobile ? mobileAiConfirmProvider() : aiConfirmProvider())

            if (!confirm) {
              return
            }
          }

          setSelectedImages([])
          _setProvider(newProvider)
        },
        onBeforeConfirm: props.onBeforeConfirm,
        onConfirm: (images: SelectedImage[], context) => {
          setSelectedImages(images)
          props.onConfirm?.(images, context)
        },
        onCancel: () => {
          setSelectedImages([])
          props.onCancel?.()
        },
        onUploadStart: async (files: File[]) => {
          await props.onUploadStart?.(files)
        },
        uploadImages: async (images: File[], context) => {
          setIsUploading(true)
          const uploadedImages = await props.uploadImages(images, context)
          setIsUploading(false)
          return uploadedImages
        },
        isUploading,
        initialQuery: props.initialQuery,
        maxCount,
        accept,
        isAIGenerating,
        setIsAIGenerating,
      }}
    >
      {props.children}
    </ImagePickerContextProvider>
  )
}

export const useImagePickerContext = () => {
  return useContext(ImagePickerContext)
}
